import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { Select } from 'primeng/select';
import { en, es, fr, kk, nl, pt, ru } from '#locale';
import { UpperCasePipe } from '@angular/common';
import { PrimeNG } from 'primeng/config';

@Component({
  selector: 'app-language-picker',
  imports: [FormsModule, TranslatePipe, Select, UpperCasePipe],
  templateUrl: './language-picker.component.html',
  styleUrl: './language-picker.component.scss',
})
export class LanguagePicker {
  // Dependency Injection
  private _translateService = inject(TranslateService);
  private _primeConfig = inject(PrimeNG);

  // Private Signals
  private _selectedLanguage = signal<string>(this._getStoredLanguage());

  // Computed values
  languagesList = computed<SelectItem[]>(() => {
    const translations = { en, es, fr, kk, nl, pt, ru };
    return Object.entries(translations).map<SelectItem<string>>(([lang, dict]) => ({
      value: lang,
      label: dict[lang] as string,
    }));
  });

  // Getter/Setter for two-way binding compatibility
  get selectedLanguage(): string {
    return this._selectedLanguage();
  }

  set selectedLanguage(value: string) {
    if (value !== this._selectedLanguage()) {
      this._selectedLanguage.set(value);
      this._applyLanguageChange(value);
    }
  }

  // Lifecycle
  constructor() {
    this._initializeLanguage();
  }

  // Public Methods
  onLanguageChange(lang: string) {
    this.selectedLanguage = lang;
  }

  // Private Methods
  private _getStoredLanguage(): string {
    return localStorage.getItem('currentLang') || 'en';
  }

  private _initializeLanguage() {
    const currentLang = this._selectedLanguage();
    this._translateService.use(currentLang);
    this._updatePrimeNGTranslations();
  }

  private _applyLanguageChange(lang: string) {
    this._translateService.use(lang);
    this._updatePrimeNGTranslations();
    localStorage.setItem('currentLang', lang);
  }

  private _updatePrimeNGTranslations() {
    this._primeConfig.setTranslation(this._translateService.instant('datepicker'));
  }
}
